import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import type { DataExportScheduleExecutionResponseRow } from '@risksmart-app/trpc/src/types';
import type { GetDataExportScheduleExecutionsSubscription } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTRPC } from 'src/utils/trpc';

import { useIsFeatureVisibleToOrg } from '@/utils/featureFlags';

/**
 * Maps TRPC data export schedule data to match the GraphQL query structure
 */
function mapTrpcDataExportScheduleExecutionsToGraphQL(
  trpcData: DataExportScheduleExecutionResponseRow[] | undefined
): GetDataExportScheduleExecutionsSubscription | undefined {
  if (!trpcData) {
    return undefined;
  }

  return {
    data_export_schedule_execution: trpcData,
  };
}

export const useGetDataExportScheduleExecutions = (skip?: boolean) => {
  const trpcEnabled = useIsFeatureVisibleToOrg('trpc');
  const trpc = useTRPC();
  const { addNotification } = useNotifications();

  const {
    data: trpcData,
    isLoading: trpcLoading,
    refetch: trpcRefetch,
    error: trpcError,
  } = useQuery({
    ...trpc.frontend.dataExport.getScheduleExecutions.queryOptions(),

    enabled: trpcEnabled && !skip,
  });

  useEffect(() => {
    if (trpcEnabled && trpcError) {
      addNotification({
        type: 'error',
        content: <>{trpcError.message}</>,
      });
    }
  }, [trpcEnabled, trpcError, addNotification]);
  const data = mapTrpcDataExportScheduleExecutionsToGraphQL(trpcData);

  return {
    loading: trpcLoading,
    data,
    refetch: () => trpcRefetch(),
    error: trpcError,
  };
};
